import { useCallback, useState } from 'react';
import { usePreservedCallback } from '@toss/react';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import { useYoutubePreview } from '@/api/ai/youtube';
import type { YoutubeVideoData } from '@/api/ai/youtube/types';
import { DialogBody, DialogFooter, DialogHeader, ResponsiveDialog } from '@/ui_v3/components/dialog';
import { YoutubeConfirmButton } from './components/YoutubeConfirmButton';
import { YoutubeUrlTextField } from './components/YoutubeUrlTextField';
import { YoutubePreviewCard } from './YoutubePreviewCard';
export interface YoutubeUrlModalProps {
  onClose: (data: YoutubeVideoData | null) => void;
  initialVideoData?: YoutubeVideoData;
}

/**
 * YouTube URL 입력 모달
 *
 * overlay-kit Promise 패턴으로 데이터 반환이 가능한 모달
 * - URL 입력 후 Enter 키로 검증 트리거 (Story 2.2에서 구현)
 * - 검증 성공 시 YoutubePreviewCard 표시 (Story 2.3에서 구현)
 * - '추가' 버튼 클릭 시 videoData 반환 (Story 2.4에서 완성)
 */
export const YoutubeUrlModal = ({
  onClose,
  initialVideoData
}: YoutubeUrlModalProps) => {
  const {
    t
  } = useTranslation(['build', 'common']);
  const [videoData, setVideoData] = useState<YoutubeVideoData | null>(() => {
    if (initialVideoData) {
      return initialVideoData;
    }
    return null;
  });

  // YouTube video preview data fetching (Story 3.3 hook integration)
  const {
    fetchPreview
  } = useYoutubePreview();
  const handleFetchVideoData = usePreservedCallback(async (videoId: string, videoUrl: string) => {
    const videoData = await fetchPreview(videoId);
    setVideoData({
      ...videoData,
      url: videoUrl
    });
    return videoData;
  });
  const handleCancel = () => {
    setVideoData(null);
  };
  const handleClose = () => {
    onClose(null);
  };
  return <ResponsiveDialog onClose={handleClose} size="md" shouldCloseOnDimClick={false} data-sentry-element="ResponsiveDialog" data-sentry-component="YoutubeUrlModal" data-sentry-source-file="YoutubeUrlModal.tsx">
      <DialogHeader title={t('build:build.ai.youtube.modalTitle')} hasCloseButton onClose={handleClose} data-sentry-element="DialogHeader" data-sentry-source-file="YoutubeUrlModal.tsx" />

      <DialogBody className="flex flex-col gap-md px-lg" data-sentry-element="DialogBody" data-sentry-source-file="YoutubeUrlModal.tsx">
        <YoutubeUrlTextField videoData={videoData} onSearch={handleFetchVideoData} onCancel={handleCancel} data-sentry-element="YoutubeUrlTextField" data-sentry-source-file="YoutubeUrlModal.tsx" />

        <div className="flex items-center justify-center overflow-hidden">
          {!videoData && <div className="flex items-center justify-center">
              <img src="/assets/build/youtube_placeholder.png" alt="YouTube" className="size-[260px]" />
            </div>}

          {videoData && <YoutubePreviewCard videoData={videoData} />}
        </div>
      </DialogBody>

      <DialogFooter className="flex justify-end" data-sentry-element="DialogFooter" data-sentry-source-file="YoutubeUrlModal.tsx">
        <YoutubeConfirmButton onConfirm={onClose} videoData={videoData} data-sentry-element="YoutubeConfirmButton" data-sentry-source-file="YoutubeUrlModal.tsx" />
      </DialogFooter>
    </ResponsiveDialog>;
};

/**
 * YoutubeUrlModal을 overlay-kit으로 여는 커스텀 훅
 *
 * overlay-kit Promise 패턴을 사용하여 모달을 열고
 * 사용자가 선택한 YouTube 영상 데이터를 반환합니다.
 *
 * @returns openYoutubeUrlModal - 모달을 여는 함수, Promise<YoutubeVideoData | null> 반환
 *
 * @example
 * const { openYoutubeUrlModal } = useOpenYoutubeUrlModal();
 * const videoData = await openYoutubeUrlModal();
 * if (videoData) {
 *   // 영상이 선택됨
 * }
 */
export const useOpenYoutubeUrlModal = () => {
  const openYoutubeUrlModal = useCallback(async (initialVideoData?: YoutubeVideoData) => {
    return overlay.openAsync<YoutubeVideoData | null>(({
      close,
      unmount
    }) => <YoutubeUrlModal onClose={data => {
      unmount();
      close(data);
    }} initialVideoData={initialVideoData} />);
  }, []);
  return {
    openYoutubeUrlModal
  };
};